/**
 * The question the portal asks before something irreversible.
 *
 * `window.confirm` draws the browser's own box, which carries the address of
 * the page and cannot be styled or worded the way the rest of the portal is.
 * This is the same question in a dialog of our own.
 *
 * A native `<dialog>` does the hard parts by itself - it traps the focus,
 * closes on Escape, makes the page behind it inert and hands the focus back
 * where it came from - so none of that is written here. Where the element
 * carries no behaviour at all the browser box is asked instead, because a
 * question nobody can answer is worse than an ugly one.
 */

import { text } from "../core/i18n";
import { query } from "../core/dom";

/** How the dialog is worded and which way its answer leans. */
export interface ConfirmOptions {
    readonly title?: string;
    readonly confirm?: string;
    readonly cancel?: string;
    readonly danger?: boolean;
}

const DIALOG_ID = "confirm-dialog";

/** Return whether the browser really implements `<dialog>`. */
export function dialogsWork(): boolean {
    return typeof HTMLDialogElement === "function" && typeof HTMLDialogElement.prototype.showModal === "function";
}

/** Return the shared dialog, building it the first time it is needed. */
export function confirmDialog(): HTMLDialogElement {
    const existing = query<HTMLDialogElement>(`#${DIALOG_ID}`);
    if (existing) {
        return existing;
    }

    const dialog = document.createElement("dialog");
    dialog.id = DIALOG_ID;
    dialog.className = "modal";

    // `method="dialog"` is what closes the dialog with the pressed button as
    // its answer, so no click handler of our own has to.
    dialog.innerHTML = `
        <form method="dialog" class="modal__inner">
            <h2 class="modal__title" data-modal-title></h2>
            <p class="modal__text" data-modal-text></p>
            <div class="modal__actions">
                <button type="submit" class="button" value="cancel" data-modal-cancel></button>
                <button type="submit" class="button button--primary" value="confirm" data-modal-confirm></button>
            </div>
        </form>
    `;
    document.body.appendChild(dialog);

    return dialog;
}

/**
 * Ask `question` and answer with what was pressed.
 *
 * Escape and the backdrop both count as a refusal: the dialog closes with no
 * answer at all, and anything which is not a plain confirmation has to be
 * read as one.
 */
export function askConfirmation(question: string, options: ConfirmOptions = {}): Promise<boolean> {
    if (!dialogsWork()) {
        return Promise.resolve(window.confirm(question));
    }

    const dialog = confirmDialog();
    const title = query<HTMLElement>("[data-modal-title]", dialog);
    const body = query<HTMLElement>("[data-modal-text]", dialog);
    const confirm = query<HTMLButtonElement>("[data-modal-confirm]", dialog);
    const cancel = query<HTMLButtonElement>("[data-modal-cancel]", dialog);

    if (!title || !body || !confirm || !cancel) {
        return Promise.resolve(window.confirm(question));
    }

    title.textContent = options.title ?? text("js.confirm.title", "Potvrdenie");
    body.textContent = question;
    confirm.textContent = options.confirm ?? text("js.confirm.confirm", "Potvrdiť");
    cancel.textContent = options.cancel ?? text("js.confirm.cancel", "Zrušiť");
    confirm.classList.toggle("button--danger", options.danger === true);
    confirm.classList.toggle("button--primary", options.danger !== true);

    dialog.returnValue = "";

    return new Promise<boolean>((resolve) => {
        const finish = (): void => {
            dialog.removeEventListener("close", finish);
            resolve(dialog.returnValue === "confirm");
        };

        dialog.addEventListener("close", finish);
        dialog.showModal();
        // The refusal is what a stray Enter or Escape should reach first.
        cancel.focus();
    });
}
